<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import TableFooterBar from '@/components/bars/TableFooterBar.vue';
import KBtn from '@/components/btns/KBtn.vue';
import TablePageFooter from '@/components/footers/TablePageFooter.vue';
import ExportAsCsvIconBtn from '@/components/iconBtns/ExportAsCsvIconBtn.vue';
import SmsDailySearchForm from '@/components/searchForms/SmsDailySearchForm.vue';
import SmsDailyTable, {
  type SmsDailyTableModel,
} from '@/components/tables/SmsDailyTable.vue';
import FormWrapper from '@/components/wrappers/FormWrapper.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import TablePageShadowWrapper from '@/components/wrappers/TablePageShadowWrapper.vue';
import useCommonRouteQuery from '@/composables/routeQueries/useCommonRouteQuery';
import useCurrentDateString from '@/composables/useCurrentDateString';
import useSmsDailyView from '@/composables/views/useSmsDailyView';
import { defCurPage, defPageSize } from '@/constants/routeQuery';

const { getCurrentQueryDate } = useCurrentDateString();

const { queryCurPage, queryPageSize, queryDivision, queryDriver, queryDate } =
  useCommonRouteQuery({
    queryCurPage: defCurPage,
    queryPageSize: defPageSize,
    queryDate: getCurrentQueryDate(),
  });

const {
  search,
  initialData,
  isFetchingData,
  isSendingMessages,
  smsDailyData,
  handleSendClick,
  downloadCsvSmsDaily,
  isDownloadingCSV,
} = useSmsDailyView(
  queryCurPage,
  queryPageSize,
  queryDate,
  queryDivision,
  queryDriver,
);

const tableModel = ref<SmsDailyTableModel>({ select: {} });

const selectedIds = computed(() => {
  const selected = [];
  for (const [id, isSelected] of Object.entries(tableModel.value.select)) {
    if (isSelected) selected.push(Number(id));
  }
  return selected;
});

function handleSendButtonClick(): void {
  const ids = selectedIds.value;
  if (ids.length === 0) return;
  void handleSendClick(ids);
}

watch([queryCurPage, queryDivision, queryDriver], () => {
  tableModel.value.select = {};
});
</script>

<template>
  <div :class="$style.smsDailyView" data-testid="smsDailyView">
    <PageContentWrapper>
      <FormWrapper :class="$style.formWrapper">
        <SmsDailySearchForm
          :initial-data="initialData"
          :loading="isFetchingData"
          :class="$style.form"
          @submit="search"
        />
      </FormWrapper>
      <div :class="$style.maxWidthWrapper">
        <TablePageShadowWrapper>
          <SmsDailyTable
            v-model="tableModel"
            :loading="isFetchingData"
            :date="queryDate?.toString()"
            :data="smsDailyData.results"
          />
          <TablePageFooter
            v-if="smsDailyData.results?.length"
            :cur-page="queryCurPage"
            :page-size="queryPageSize"
            :total="smsDailyData.count"
          >
            <ExportAsCsvIconBtn
              :disabled="isFetchingData || !smsDailyData.results?.length"
              :loading="isDownloadingCSV"
              @click="downloadCsvSmsDaily"
            />
          </TablePageFooter>
        </TablePageShadowWrapper>
      </div>
    </PageContentWrapper>
    <TableFooterBar>
      <KBtn
        size="lg"
        append-icon="mdi-arrow-down"
        :disabled="isFetchingData || selectedIds.length === 0"
        :loading="isSendingMessages"
        @click="handleSendButtonClick"
      >
        Send<template v-if="selectedIds.length > 0">
          ({{ selectedIds.length }})</template
        >
      </KBtn>
    </TableFooterBar>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.smsDailyView {
}
  .formWrapper {
    padding: 2.4rem 1.5rem 0.5rem 1.5rem;
    max-width: variables.$tableWrapperWidth;
    margin: 2rem 0;
  }
  .maxWidthWrapper {
    max-width: variables.$tableWrapperWidth;
  }
  .h3 {
    font-weight: 500;
    margin: 1.5rem 0 1.3rem 0;
  }
</style>
